import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";
import { useApiErrorMessage } from "../../i18n/useApiErrorMessage";

import Box from "@mui/material/Box";
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
  MuiEvent,
} from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import SearchBar from "material-ui-search-bar";

import { MaterialIcons } from "../../icons/Material/MaterialIcons";
import DataTable from "../table/DataTable";
import YearMonthFilter from "../YearMonthFilter/YearMonthFilter";

import {
  getPeriodType,
  getCompanyPeriodsTypes,
} from "../../services/CompaniesService";

import { DrawerContext } from "../../context/drawer/DrawerContext";

import { useEmpresaStore } from "../../store/empresaStore";
import { useParametrosStore } from "../../store/parametrosStore";

import { TipoPeriodoRowsType } from "../../types";
import HtmlTooltip from "../HtmlTooltip/HtmlTooltip";
import { useHasPermissions } from "react-simple-rbac";

const getDurationArray = () => [
  { id: 0, value: i18n.t("duration.monthly", { ns: "parametros" }) },
  { id: 1, value: i18n.t("duration.biweekly", { ns: "parametros" }) },
]

//1: success, 2:pending, 3:cancel
const getAction = (
  params: GridRenderCellParams,
  icon: keyof typeof MaterialIcons
) => {
  const Icon = MaterialIcons[icon || "PollIcon"];
  if (params.value === "2") {
    return (
      <Box
        sx={{
          display: "flex",
          placeItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Button variant="contained" color="primary">
          {i18n.t("actions.processNow", { ns: "common" })}
        </Button>
        <Icon />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        placeItems: "center",
        justifyContent: "flex-end",
        width: "100%",
      }}
    >
      <Icon />
    </Box>
  );
};

const getDuration = (params: GridValueFormatterParams) => {
  return getDurationArray().find((duration) => duration.id === params.value)?.value;
};


const TiposPeriodos = () => {
  const { t } = useTranslation("parametros");
  const getApiErrorMessage = useApiErrorMessage();
  const [searched, setSearched] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [periods, setPeriods] = useState<TipoPeriodoRowsType[]>([]);
  const [orignialPeriods, setOriginalPeriods] = useState<TipoPeriodoRowsType[]>(
    []
  );
  const [filterYear, setFilterYear] = useState<number | ''>(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | ''>(new Date().getMonth() + 1);

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const setAllPeriodos = useParametrosStore((state) => state.setAllPeriodos);

  const { changeLink } = useContext(DrawerContext);

  const empresaId = useEmpresaStore((state) => state.empresaId, shallow);

  const canCreatePeriodType = useHasPermissions(["create:period-type"]);


  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("periodos.columns.name"),
      flex: 1,
      editable: true,
      headerClassName: "theme--header",
    },
    {
      field: "duration",
      headerName: t("periodos.columns.duration"),
      flex: 1,
      editable: true,
      headerClassName: "theme--header",
      valueFormatter: getDuration,
    },
    {
      field: "day_limit",
      headerName: t("periodos.columns.dayLimit"),
      headerClassName: "theme--header",
      flex: 1,
      renderCell(params) {
        const row = params.row;
        if (row.reference_date && row.reference_date !== "0001-01-01T00:00:00Z") {
          return new Date(row.reference_date).toLocaleDateString("es-AR");
        }
        return params.value;
      },
    },
    {
      field: "periods_before",
      headerName: t("periodos.columns.advance"),
      headerClassName: "theme--header",
      flex: 1,
    },
    {
      field: "action",
      headerName: t("periodos.columns.action"),
      type: "number",
      flex: 1,
      editable: false,
      headerClassName: "theme--header",
      renderCell(params) {
        return getAction(
          params,
          changeLink === 1 ? "EditIcon" : "VisibilityOutlined"
        );
      },
    },
  ];

  const requestSearch = (searchedVal: string) => {
    const searchPeriodType = [...orignialPeriods];
    if (searchedVal.length > 2) {
      const filteredRows = searchPeriodType.filter((row) => {
        return row.name.toLowerCase().includes(searchedVal.toLowerCase());
      });
      setPeriods(filteredRows);
    } else {
      setPeriods(orignialPeriods);
    }
  };

  const addPeriod = () => {
    const query = filterYear && filterMonth ? `?year=${filterYear}&month=${filterMonth}` : '';
    navigate(`/parametrosportal/creartipodeperiodo${query}`);
  };

  const cancelSearch = () => {
    setSearched("");
    requestSearch(searched);
  };

  const handleOnCellClick = (
    params: GridCellParams,
    event: MuiEvent<React.MouseEvent>
  ) => {
    const query = filterYear && filterMonth ? `?year=${filterYear}&month=${filterMonth}` : '';
    if (changeLink === 1) {
      navigate(`/parametrosportal/tipodeperiodo/periodo/${params.id}${query}`);
    } else {
      navigate(`/parametros/tipodeperiodo/periodo/${params.row.id}${query}`);
    }
  };

  const filterByYearMonth = useCallback(
    (data: TipoPeriodoRowsType[]) => {
      if (!filterYear && !filterMonth) return data;
      return data.filter((pt: any) => {
        if (!pt.reference_date || pt.reference_date === "0001-01-01T00:00:00Z") {
          return true;
        }
        const d = new Date(pt.reference_date);
        if (filterYear && d.getFullYear() !== filterYear) return false;
        if (filterMonth && d.getMonth() + 1 !== filterMonth) return false;
        return true;
      });
    },
    [filterYear, filterMonth]
  );

  const getPeriod = useCallback(async () => {
    setIsLoading(true);
    const token = await getAccessTokenSilently();

    if (changeLink === 1) {
      getPeriodType(token, filterYear || undefined, filterMonth || undefined)
        .then((result) => {
          setPeriods(result.data);
          setOriginalPeriods(result.data);
          setAllPeriodos(result.data);
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e.response.data.error);
          setErrorMessage(getApiErrorMessage(e));
          setFetchError(true);
          setPeriods([]);
          setOriginalPeriods([]);
          setAllPeriodos([]);
          setIsLoading(false);
        });
    } else {
      getCompanyPeriodsTypes(token, empresaId)
        .then((result) => {
          setOriginalPeriods(result.data);
          setPeriods(filterByYearMonth(result.data));
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e.response.data.error);
          setErrorMessage(getApiErrorMessage(e));
          setFetchError(true);
          setPeriods([]);
          setOriginalPeriods([]);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLink, filterYear, filterMonth]);

  // Client-side filter for company view when year/month changes
  useEffect(() => {
    if (changeLink !== 1 && orignialPeriods.length > 0) {
      setPeriods(filterByYearMonth(orignialPeriods));
    }
  }, [changeLink, filterYear, filterMonth, filterByYearMonth, orignialPeriods]);

  useEffect(() => {
    getPeriod();
  }, [getPeriod]);

  return (
    <Box
      sx={{
        height: "100%",
        "& .theme--header": {
          backgroundColor: "#EEF1FC",
        },
      }}
    >
      <Grid container spacing={2} sx={{ py: "24px" }}>
        <Grid display="flex" sx={{ placeItems: "center" }} item xs={4}>
          <Box sx={{ marginRight: 4, width: "459px" }}>
            <SearchBar
              value={searched}
              onChange={(searchVal: string) => requestSearch(searchVal)}
              onCancelSearch={() => cancelSearch()}
              placeholder={t("periodos.searchPlaceholder")}
              style={{ boxShadow: "none" }}
            />
          </Box>
        </Grid>
        <Grid display="flex" sx={{ placeItems: "center", gap: 2 }} item xs={4}>
          <YearMonthFilter
            year={filterYear}
            month={filterMonth}
            onYearChange={setFilterYear}
            onMonthChange={setFilterMonth}
          />
        </Grid>
        {changeLink === 1 && (
          <Grid item xs={4}>
            <Grid container display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                {/* <Button
                variant="contained"
                startIcon={<MaterialIcons.FilterAltIcon />}
                sx={{
                  backgroundColor: "white",
                  color: "black",
                  "&:hover": { color: "white" },
                }}
              >
                Filtrar
              </Button> */}
                {canCreatePeriodType ?
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => addPeriod()}
                  >
                    {t("periodos.createButton")}
                  </Button>

                  : (
                    <HtmlTooltip
                      title={<b>{t("periodos.noCreatePermission")}</b>}
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => addPeriod()}
                          disabled
                        >
                          {t("periodos.createButton")}
                        </Button>
                      </span>
                    </HtmlTooltip>)
                }


              </Stack>
            </Grid>
          </Grid>
        )}
      </Grid>
      <DataTable
        columns={columns}
        rows={periods}
        loading={isLoading}
        noFilterRowsMessage={t("periodos.noFilterRowsMessage")}
        noRowsMessage={fetchError ? errorMessage : t("periodos.noRowsMessage")}
        noRowClick
        onCellClick={handleOnCellClick}
      />
    </Box>
  );
};

export default TiposPeriodos;
